/**
 * One weather briefing per plan, shared by the two sections that show it: the
 * ATIS / Platzwetter block (B6) fills its rows from it, the Wetter-Beratung block
 * (V6) takes the raw TAF and METAR into its lines.
 *
 * The provider holds the fetch state and nothing else — every value it produces
 * lands in the PlanStore, which is what the form, the PDF and autosave read. It
 * wraps the form's `children` element unchanged, so a fetch re-renders the two
 * components that call {@link useWx} and not the four hundred inputs around them.
 *
 * **It fetches by itself.** A briefing nobody pressed a button for is what a
 * pilot opening a plan expects to find; so the stations of `von` / `nach` /
 * `alt_name` are read as soon as they name an aerodrome, again every ten minutes
 * for as long as the plan is open and the tab is in front, and on demand from the
 * one ⟳ in the section header. Three things follow from the fetch without being
 * asked for either — the ATIS rows, the raw bulletins in the V6 block, and, for
 * an aerodrome that publishes nothing, the nearest station that does. All three
 * are ordinary derived values: typing over one takes the cell for good, clearing
 * one hands it back and keeps it back.
 */
import {
  createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode,
} from 'react';

import { useReadOnly } from './components/Field';
import { usePublishFreshness } from './freshness';
import { useT } from './i18n';
import { usePlanRev, usePlanStore } from './planContext';
import { useAutoLoad } from './useAutoLoad';
import {
  briefPlan, newBriefingMemo, planStations,
  type BriefingMemo, type NearbyFound, type WetterPush, type WxLoad,
} from './wx';

/** ICAO cells are typed into; one debounce covers "EDD" → "EDDK". */
const DEBOUNCE_MS = 800;
/**
 * How often an open plan re-reads the briefing.
 *
 * A METAR is issued every half hour and a SPECI whenever the weather says so, so
 * ten minutes is inside the cycle without being a poll: six requests an hour for
 * a plan somebody is looking at, and none at all for a tab in the background.
 */
const EVERY_MS = 10 * 60_000;

export interface WxView {
  /** the ICAO ids of `von` / `nach` / `alt_name`, de-duplicated */
  stations: string[];
  /** what the last fetch in THIS session returned; null before the first one */
  load: WxLoad | null;
  /**
   * The nearest reporting station for each aerodrome NOAA publishes nothing for,
   * looked up right after the briefing that found the gap. What the weather is
   * doing 24 NM away is a fact the pilot should not have to request, and it costs
   * one bounded call per silent aerodrome.
   */
  nearby: NearbyFound[];
  /** true while the nearby lookups behind the last briefing are still out */
  nearbyBusy: boolean;
  /** the ATIS row each neighbour was written into, by its id; 0 = no room */
  nearbyTaken: Record<string, number>;
  busy: boolean;
  error: unknown;
  /** what the last unattended pass wrote into the Wetter-Beratung block */
  pushed: WetterPush | null;
  /** read it again now — the section header's ⟳ */
  refresh(): void;
}

const WxContext = createContext<WxView | null>(null);

export function WxProvider({ children }: { children: ReactNode }) {
  const store = usePlanStore();
  const t = useT();
  const rev = usePlanRev();
  const readOnly = useReadOnly();
  const [load, setLoad] = useState<WxLoad | null>(null);
  const [nearby, setNearby] = useState<NearbyFound[]>([]);
  const [nearbyBusy, setNearbyBusy] = useState(false);
  const [nearbyTaken, setNearbyTaken] = useState<Record<string, number>>({});
  const [pushed, setPushed] = useState<WetterPush | null>(null);
  /**
   * The V6 lines and the neighbours this tab has already written down.
   *
   * It is what makes "unattended" survivable: a line is written once, refreshed
   * in place while it is still ours, and never put back after the pilot deletes
   * it. Without it the ten-minute pass would undo every deletion.
   */
  const wrote = useRef<BriefingMemo>(newBriefingMemo());

  const stations = useMemo(() => planStations(id => store.val(id)), [store, rev]);
  // the identity of the context value must not change on every keystroke, only
  // when the set of stations really does
  const key = stations.join(',');

  /**
   * The whole unattended pass — `briefPlan` in `wx.ts` does the work; what is
   * here is the part React owns: what the panel shows while it runs, and the two
   * memos that make repeating it safe.
   */
  const run = useCallback(
    async (ids: string[], signal: AbortSignal): Promise<void> => {
      if (!ids.length) return;
      try {
        const pass = await briefPlan(store, ids, wrote.current, {
          signal,
          label: f => t('wx.nearby.cell', { id: f.id, dist: Math.round(f.distNm), of: f.for }),
          onNearby: () => setNearbyBusy(true),
        });
        setLoad(pass.load);
        setPushed(pass.push.values.size || pass.push.dropped.length ? pass.push : null);
        setNearby(prev => mergeNearby(prev, pass.nearby, pass.load.missing, ids));
        if (Object.keys(pass.taken).length) setNearbyTaken(prev => ({ ...prev, ...pass.taken }));
        // a failed fallback is not a failed briefing: the METARs are on the form
        if (pass.nearbyError) tracing(pass.nearbyError);
      } finally {
        setNearbyBusy(false);
      }
    },
    [store, t],
  );

  const auto = useAutoLoad({
    // the shared read-only page mounts this provider too, and may fetch nothing
    signature: readOnly ? '' : key,
    debounceMs: DEBOUNCE_MS,
    everyMs: EVERY_MS,
    staleMs: EVERY_MS,
    run: ({ signal }) => run(stations, signal),
  });

  usePublishFreshness(
    'wx',
    useMemo(
      () => (readOnly ? null : { busy: auto.busy, error: auto.error, at: auto.at, refresh: auto.refresh }),
      [readOnly, auto.busy, auto.error, auto.at, auto.refresh],
    ),
  );

  const value = useMemo<WxView>(
    () => ({
      stations,
      load,
      busy: auto.busy,
      error: auto.error,
      pushed,
      nearby,
      nearbyBusy,
      nearbyTaken,
      refresh: auto.refresh,
    }),
    // `stations` is a fresh array on every document revision — `key` is the dependency
    // that says whether anything about it changed
    [key, load, nearby, nearbyBusy, nearbyTaken, pushed, auto.busy, auto.error, auto.refresh],
  );

  return <WxContext.Provider value={value}>{children}</WxContext.Provider>;
}

/**
 * The neighbours a fetch found, folded into what is already on screen.
 *
 * A station that this fetch found a report for no longer needs one at all;
 * anything else stays, which is what keeps a refresh of one aerodrome from
 * blanking another's fallback.
 */
function mergeNearby(
  prev: readonly NearbyFound[],
  found: readonly NearbyFound[],
  missing: readonly string[],
  asked: readonly string[],
): NearbyFound[] {
  const gone = new Set(asked.map(i => i.toUpperCase()));
  const still = new Set(missing.map(i => i.toUpperCase()));
  const kept = prev.filter(n => !gone.has(n.for) || still.has(n.for));
  const fresh = new Set(found.map(n => n.for));
  return [...kept.filter(n => !fresh.has(n.for)), ...found];
}

/** A fallback that failed is logged and not shown: the briefing itself succeeded. */
function tracing(e: unknown): void {
  if (import.meta.env.DEV) console.warn('nearby lookup failed', e);
}

/** null outside a {@link WxProvider} — the shared read-only view has no fetching. */
export function useWx(): WxView | null {
  return useContext(WxContext);
}
